import { useMemo, useState } from 'react'
import { Archive, MessageSquare, Plus, X } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore } from '@/store/social'
import { describeError, publicUser } from '@/lib/auth'
import { P, basePermissions, channelPermissions, has } from '@/lib/permissions'
import { isUnread, memberName, plural, threadsOf } from '@/lib/guildUi'
import type { Channel, Member } from '@/types/api'
import { cx } from '@/lib/cx'
import { isMobileNow } from '@/lib/mobile'
import { ThreadModal } from '../chat/ThreadModal'
import member from '../MemberList.module.css'
import styles from './ThreadsPanel.module.css'

/** Ветки канала в правой колонке: активные из стора, архивные — по запросу. */
export function ThreadsPanel({ guildId, channelId }: { guildId: string; channelId: string }) {
  const setThreadsPanel = useAppStore((s) => s.setThreadsPanel)
  const setView = useAppStore((s) => s.setView)
  const view = useAppStore((s) => s.view)
  const me = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channels = useGuildStore((s) => s.channels[guildId])
  const members = useGuildStore((s) => s.members[guildId])
  const loadArchivedThreads = useGuildStore((s) => s.loadArchivedThreads)
  const readStates = useSocialStore((s) => s.readStates)
  const channel = channels?.[channelId]
  const active = useMemo(() => threadsOf(channels, channelId), [channels, channelId])
  const [archived, setArchived] = useState<{ channelId: string; ids: string[]; hasMore: boolean; loading: boolean; error: string | null } | null>(null)
  const arch = archived?.channelId === channelId ? archived : null
  const [creating, setCreating] = useState(false)

  const self: Member | undefined = me ? (members?.[me.id] ?? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null }) : undefined
  const canCreate = Boolean(guild && self && has(channelPermissions(guild, self, channel), P.CREATE_PUBLIC_THREADS)) && Boolean(guild && self && has(basePermissions(guild, self), P.VIEW_CHANNEL))
  const selectedId = view.kind === 'guild' ? view.channelId : null

  const loadArchived = async (before?: string) => {
    const prev = before ? (arch?.ids ?? []) : []
    setArchived({ channelId, ids: prev, hasMore: false, loading: true, error: null })
    try {
      const res = await loadArchivedThreads(guildId, channelId, before)
      setArchived({ channelId, ids: [...prev, ...res.threads.map((t) => t.id)], hasMore: res.has_more, loading: false, error: null })
    } catch (err) {
      setArchived({ channelId, ids: prev, hasMore: false, loading: false, error: describeError(err).message })
    }
  }
  // ветка, которую вернули из архива сообщением, уходит из списка сразу
  const archivedList = (arch?.ids ?? []).map((id) => channels?.[id]).filter((c): c is Channel => Boolean(c?.thread_metadata?.archived))
  const open = (id: string) => {
    setView({ kind: 'guild', guildId, channelId: id })
    // на телефоне список веток — слой поверх чата: открытая ветка оказалась бы под ним
    if (isMobileNow()) setThreadsPanel(null)
  }
  const ownerName = (th: Channel) => (th.owner_id && members?.[th.owner_id] ? memberName(members[th.owner_id]!) : null)

  const item = (th: Channel) => {
    const n = th.message_count ?? 0
    const owner = ownerName(th)
    return (
      <button key={th.id} type="button" className={cx(styles.thread, th.id === selectedId && styles.threadSelected)} onClick={() => open(th.id)}>
        <span className={styles.threadHead}>
          <MessageSquare size={16} className={styles.icon} />
          <span className={cx(styles.name, isUnread(th, readStates[th.id]) && styles.unread)}>{th.name}</span>
        </span>
        <span className={styles.meta}>
          {n} {plural(n, 'сообщение', 'сообщения', 'сообщений')}
          {owner ? ` · ${owner}` : ''}
          {th.member ? ' · вы участвуете' : ''}
          {th.thread_metadata?.archived ? ' · в архиве' : ''}
        </span>
      </button>
    )
  }

  return (
    <aside className={cx(member.list, styles.panel, 'scroller')} aria-label="Ветки канала">
      <div className={member.wco} aria-hidden="true" />
      <div className={styles.head}>
        <span className={styles.title}>Ветки #{channel?.name ?? ''}</span>
        <button type="button" className={styles.close} aria-label="Закрыть ветки" onClick={() => setThreadsPanel(null)}><X size={18} /></button>
      </div>
      {canCreate ? (
        <button type="button" className={styles.create} onClick={() => setCreating(true)}>
          <Plus size={18} /> Создать ветку
        </button>
      ) : null}
      <section aria-label="Активные ветки">
        <h3 className={styles.group}>Активные — {active.length}</h3>
        {active.length === 0 ? <p className={styles.empty}>Активных веток нет. Ветку можно начать от любого сообщения в канале.</p> : null}
        {active.map(item)}
      </section>
      <section aria-label="Архивные ветки">
        <h3 className={styles.group}><Archive size={12} /> Архив</h3>
        {arch === null ? (
          <button type="button" className={styles.more} onClick={() => void loadArchived()}>Показать архивные</button>
        ) : (
          <>
            {archivedList.length === 0 && !arch.loading && !arch.error ? <p className={styles.empty}>В архиве пусто.</p> : null}
            {archivedList.map(item)}
            {arch.error ? <p className={styles.error} role="alert">{arch.error}</p> : null}
            {arch.loading ? <p className={styles.empty}>Загружаем…</p> : null}
            {arch.hasMore && !arch.loading ? (
              <button type="button" className={styles.more} onClick={() => void loadArchived(archivedList[archivedList.length - 1]?.thread_metadata?.archive_timestamp ?? undefined)}>Ещё</button>
            ) : null}
          </>
        )}
      </section>
      {creating && channel ? (
        <ThreadModal channelId={channelId} channelName={channel.name ?? ''} message={null} onClose={() => setCreating(false)} onCreated={(th) => { setCreating(false); open(th.id) }} />
      ) : null}
    </aside>
  )
}
